import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { stripePromise } from "../../config/stripePublishable.js";
import PaymentService from "../../services/payment-services.js";
import { createSubscriptionWithStripe, cancelSubscriptionWithStripe, getCompleteSubscriptionData, resumeSubscriptionRenewal } from "../../services/subscription-payment-service.js";
import PaymentStatusModal from "../../components/UI/PaymentStatusModal/PaymentStatusModal";
import { useAuth } from "../../context/authContext";
import { logClientError } from "../../utils/clientLog.js";
import styles from "./CheckoutPage.module.css";
import softindexLogo from "../../assets/images/softindex-logo.webp";

const getCurrencySymbol = currency => {
  switch (currency?.toLowerCase()) {
    case "eur":
      return "€";
    case "ron":
      return "RON";
    default:
      return "€";
  }
};
const TIER_PREFIX = {
  Basic: "PLBSC",
  Premium: "PLPRM",
  Enterprise: "PLENT"
};
function generatePlanId(tier) {
  const prefix = TIER_PREFIX[tier] || "PLUNK";
  const now = new Date();
  const yy = String(now.getFullYear()).slice(-2);
  const dd = String(now.getDate()).padStart(2, "0");
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const randomNum = Math.floor(Math.random() * 9000) + 1000;
  return `${prefix}${yy}${dd}${mm}${randomNum}`;
}
function normalizeCheckoutSubscriptionTier(details) {
  if (!details) return "Basic";
  const parseToken = raw => {
    if (raw == null) return null;
    const s = String(raw).trim().replace(/^\uFEFF/, "");
    if (!s) return null;
    const token = s.split(/\s+/)[0];
    const k = token.toLowerCase();
    if (k.startsWith("basic")) return "Basic";
    if (k.startsWith("premium")) return "Premium";
    if (k.startsWith("enterprise")) return "Enterprise";
    return null;
  };
  const fromPlanId = pid => {
    const p = String(pid || "").trim().toLowerCase();
    const m = p.match(/^(basic|premium|enterprise)(?:_monthly)?$/);
    if (!m) return null;
    if (m[1] === "basic") return "Basic";
    if (m[1] === "premium") return "Premium";
    return "Enterprise";
  };
  const fromSoftindexProjectId = projId => {
    const u = String(projId || "").trim().toUpperCase();
    if (u.startsWith("PLBSC")) return "Basic";
    if (u.startsWith("PLPRM")) return "Premium";
    if (u.startsWith("PLENT")) return "Enterprise";
    return null;
  };
  return fromSoftindexProjectId(details.projectId) || parseToken(details.planTier) || parseToken(details.projectTier) || parseToken(details.planName) || parseToken(details.tier) || fromPlanId(details.planId) || "Basic";
}
const CheckoutForm = ({
  paymentIntent,
  paymentDetails,
  onPaymentSuccess,
  onPaymentError,
  paymentMethods,
  onCreatePaymentIntent,
  onCreateSubscription,
  onCancel,
  onRefreshPaymentMethods,
  paymentMethodsListError
}) => {
  const {
    t
  } = useTranslation("common");
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [showCancelSubscriptionPanel, setShowCancelSubscriptionPanel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelFeedback, setCancelFeedback] = useState("");
  const [isCancelProcessing, setIsCancelProcessing] = useState(false);
  const [isResumeProcessing, setIsResumeProcessing] = useState(false);
  const [settingDefaultPm, setSettingDefaultPm] = useState(false);
  const [actionError, setActionError] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("saved");
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState("");
  const [currentPaymentIntent, setCurrentPaymentIntent] = useState(paymentIntent);
  const [customerType, setCustomerType] = useState("person");
  const [cardHolderInfo, setCardHolderInfo] = useState({
    name: "",
    email: "",
    address: {
      line1: "",
      city: "",
      state: "",
      country: ""
    },
    phone: "",
    companyName: "",
    vatNumber: "",
    registrationNumber: "",
    companyAddress: {
      line1: "",
      city: "",
      state: "",
      country: ""
    }
  });
  const [productInfo] = useState({
    productName: paymentDetails?.type === "domain_extension" ? paymentDetails?.domain || "" : paymentDetails?.existingProductName || paymentDetails?.productName || ""
  });
  const [errors, setErrors] = useState({});
  const countries = [{
    code: "US",
    name: "United States"
  }, {
    code: "CA",
    name: "Canada"
  }, {
    code: "GB",
    name: "United Kingdom"
  }, {
    code: "DE",
    name: "Germany"
  }, {
    code: "FR",
    name: "France"
  }, {
    code: "IT",
    name: "Italy"
  }, {
    code: "ES",
    name: "Spain"
  }, {
    code: "NL",
    name: "Netherlands"
  }, {
    code: "RO",
    name: "Romania"
  }, {
    code: "AU",
    name: "Australia"
  }, {
    code: "JP",
    name: "Japan"
  }];
  useEffect(() => {
    if (paymentMethods && paymentMethods.length > 0) {
      const defaultMethod = paymentMethods.find(method => method.isDefault) || paymentMethods[0];
      const hasCompleteBillingInfo = defaultMethod.billingInfo && defaultMethod.billingInfo.fullName?.trim() && defaultMethod.billingInfo.fullName !== "Unknown" && defaultMethod.billingInfo.email?.trim() && defaultMethod.billingInfo.email !== "Unknown" && defaultMethod.billingInfo.address?.street?.trim() && defaultMethod.billingInfo.address.street !== "Unknown" && defaultMethod.billingInfo.address?.city?.trim() && defaultMethod.billingInfo.address.city !== "Unknown" && defaultMethod.billingInfo.address?.country?.trim() && defaultMethod.billingInfo.address.country !== "Unknown";
      if (hasCompleteBillingInfo) {
        setSelectedPaymentMethodId(defaultMethod.id);
        setPaymentMethod("saved");
        const customerTypeFromMethod = defaultMethod.billingInfo.customerType || "person";
        setCustomerType(customerTypeFromMethod);
        setCardHolderInfo({
          name: defaultMethod.billingInfo.fullName || "",
          email: defaultMethod.billingInfo.email || "",
          phone: defaultMethod.billingInfo.phone || "",
          address: {
            line1: defaultMethod.billingInfo.address?.street || "",
            city: defaultMethod.billingInfo.address?.city || "",
            state: defaultMethod.billingInfo.address?.state || "",
            country: defaultMethod.billingInfo.address?.country || "",
            postal_code: defaultMethod.billingInfo.address?.zip || ""
          },
          companyName: defaultMethod.billingInfo.companyName || "",
          vatNumber: defaultMethod.billingInfo.vatNumber || "",
          registrationNumber: defaultMethod.billingInfo.registrationNumber || "",
          companyAddress: {
            line1: defaultMethod.billingInfo.address?.street || "",
            city: defaultMethod.billingInfo.address?.city || "",
            state: defaultMethod.billingInfo.address?.state || "",
            country: defaultMethod.billingInfo.address?.country || "",
            postal_code: defaultMethod.billingInfo.address?.zip || ""
          }
        });
      } else {
        setPaymentMethod("new");
        setSelectedPaymentMethodId("");
      }
    } else {
      setPaymentMethod("new");
    }
  }, [paymentMethods]);
  const handleCardHolderChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setCardHolderInfo(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setCardHolderInfo(prev => ({
        ...prev,
        [field]: value
      }));
    }
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };
  const handlePaymentMethodChange = methodId => {
    setSelectedPaymentMethodId(methodId);
    if (paymentMethod === "saved" && paymentMethods) {
      const selectedMethod = paymentMethods.find(method => method.id === methodId);
      if (selectedMethod?.billingInfo) {
        const hasCompleteBillingInfo = selectedMethod.billingInfo && selectedMethod.billingInfo.fullName?.trim() && selectedMethod.billingInfo.fullName !== "Unknown" && selectedMethod.billingInfo.email?.trim() && selectedMethod.billingInfo.email !== "Unknown" && selectedMethod.billingInfo.address?.street?.trim() && selectedMethod.billingInfo.address.street !== "Unknown" && selectedMethod.billingInfo.address?.city?.trim() && selectedMethod.billingInfo.address.city !== "Unknown" && selectedMethod.billingInfo.address?.country?.trim() && selectedMethod.billingInfo.address.country !== "Unknown";
        if (hasCompleteBillingInfo) {
          const customerTypeFromMethod = selectedMethod.billingInfo.customerType || "person";
          setCustomerType(customerTypeFromMethod);
          setCardHolderInfo({
            name: selectedMethod.billingInfo.fullName || "",
            email: selectedMethod.billingInfo.email || "",
            phone: selectedMethod.billingInfo.phone || "",
            address: {
              line1: selectedMethod.billingInfo.address?.street || "",
              city: selectedMethod.billingInfo.address?.city || "",
              state: selectedMethod.billingInfo.address?.state || "",
              country: selectedMethod.billingInfo.address?.country || "",
              postal_code: selectedMethod.billingInfo.address?.zip || ""
            },
            companyName: selectedMethod.billingInfo.companyName || "",
            vatNumber: selectedMethod.billingInfo.vatNumber || "",
            registrationNumber: selectedMethod.billingInfo.registrationNumber || "",
            companyAddress: {
              line1: selectedMethod.billingInfo.address?.street || "",
              city: selectedMethod.billingInfo.address?.city || "",
              state: selectedMethod.billingInfo.address?.state || "",
              country: selectedMethod.billingInfo.address?.country || "",
              postal_code: selectedMethod.billingInfo.address?.zip || ""
            }
          });
          setErrors({});
        } else {
          setErrors({
            paymentMethod: "Selected payment method has incomplete billing information. Please use a new card or update your payment method."
          });
        }
      }
    }
  };
  const handleCustomerTypeChange = type => {
    setCustomerType(type);
    setErrors({});
    if (type === "person") {
      setCardHolderInfo(prev => ({
        ...prev,
        companyName: "",
        vatNumber: "",
        registrationNumber: "",
        companyAddress: {
          line1: "",
          city: "",
          state: "",
          country: ""
        }
      }));
    }
    if (type === "company") {
      setCardHolderInfo(prev => ({
        ...prev,
        address: {
          line1: "",
          city: "",
          state: "",
          country: ""
        }
      }));
    }
  };
  const validateForm = () => {
    const newErrors = {};
    if (paymentMethod === "saved" && !selectedPaymentMethodId) {
      newErrors.paymentMethod = "Please select a payment method";
      setErrors(newErrors);
      return false;
    }
    if (paymentMethod === "saved" && selectedPaymentMethodId) {
      const selectedMethod = paymentMethods.find(method => method.id === selectedPaymentMethodId);
      if (!selectedMethod?.billingInfo) {
        newErrors.paymentMethod = "Selected payment method has no billing information";
        setErrors(newErrors);
        return false;
      }
      const billingInfo = selectedMethod.billingInfo;
      if (!billingInfo.fullName?.trim() || billingInfo.fullName === "Unknown") {
        newErrors.paymentMethod = "Saved payment method has incomplete billing information. Please use a new card or update your payment method.";
      }
      if (!billingInfo.email?.trim() || billingInfo.email === "Unknown") {
        newErrors.paymentMethod = "Saved payment method has incomplete billing information. Please use a new card or update your payment method.";
      }
      if (!billingInfo.address?.street?.trim() || billingInfo.address.street === "Unknown") {
        newErrors.paymentMethod = "Saved payment method has incomplete billing information. Please use a new card or update your payment method.";
      }
      if (!billingInfo.address?.city?.trim() || billingInfo.address.city === "Unknown") {
        newErrors.paymentMethod = "Saved payment method has incomplete billing information. Please use a new card or update your payment method.";
      }
      if (!billingInfo.address?.country?.trim() || billingInfo.address.country === "Unknown") {
        newErrors.paymentMethod = "Saved payment method has incomplete billing information. Please use a new card or update your payment method.";
      }
      if (selectedMethod.billingInfo.customerType === "company") {
        if (!selectedMethod.billingInfo.companyName?.trim()) {
          newErrors.paymentMethod = "Saved payment method missing company name";
        }
        if (!selectedMethod.billingInfo.vatNumber?.trim()) {
          newErrors.paymentMethod = "Saved payment method missing VAT number";
        }
        if (!selectedMethod.billingInfo.registrationNumber?.trim()) {
          newErrors.paymentMethod = "Saved payment method missing registration number";
        }
      }
    }
    if (paymentMethod === "new") {
      if (!cardHolderInfo.name.trim()) {
        newErrors.cardHolderName = "Full name is required";
      }
      if (!cardHolderInfo.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cardHolderInfo.email)) {
        newErrors.email = "Please enter a valid email address";
      }
      if (customerType === "person") {
        if (!cardHolderInfo.address.line1.trim()) {
          newErrors["address.line1"] = "Billing address is required";
        }
        if (!cardHolderInfo.address.city.trim()) {
          newErrors["address.city"] = "City is required";
        }
        if (!cardHolderInfo.address.country.trim()) {
          newErrors["address.country"] = "Country is required";
        }
      }
      if (!cardHolderInfo.phone.trim()) {
        newErrors.phone = "Phone number is required";
      }
      if (customerType === "company") {
        if (!cardHolderInfo.companyName.trim()) {
          newErrors.companyName = "Company name is required";
        }
        if (!cardHolderInfo.vatNumber.trim()) {
          newErrors.vatNumber = "VAT number is required";
        }
        if (!cardHolderInfo.registrationNumber.trim()) {
          newErrors.registrationNumber = "Registration number is required";
        }
        if (!cardHolderInfo.companyAddress.line1.trim()) {
          newErrors["companyAddress.line1"] = "Company address is required";
        }
        if (!cardHolderInfo.companyAddress.city.trim()) {
          newErrors["companyAddress.city"] = "Company city is required";
        }
        if (!cardHolderInfo.companyAddress.country.trim()) {
          newErrors["companyAddress.country"] = "Company country is required";
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const createPaymentIntent = async backendData => {
    try {
      if (onCreatePaymentIntent) {
        return await onCreatePaymentIntent(backendData);
      }
      if (paymentDetails?.type === "domain_extension") {
        const domainData = {
          domainId: paymentDetails.domainId || "",
          domainName: paymentDetails.domain,
          years: paymentDetails.years,
          pricePerYear: paymentDetails.pricePerYear || paymentDetails.amount / paymentDetails.years,
          currency: paymentDetails.currency || "eur",
          planId: paymentDetails.planId || "",
          currentExpirationDate: paymentDetails.currentExpirationDate || new Date().toISOString(),
          clientID: backendData.clientID || "CL-0001",
          fullName: backendData.fullName || "Customer",
          emailAddress: backendData.emailAddress || "customer@example.com",
          billingAddress: backendData.billingAddress || "Default Address",
          city: backendData.city || "Default City",
          stateRegion: backendData.stateRegion || "",
          country: backendData.country || "US",
          phone: backendData.phone || "0000000000",
          customerType: backendData.customerType || "person",
          contactName: backendData.contactName || backendData.fullName || "Customer",
          companyName: backendData.companyName || "",
          vatNumber: backendData.vatNumber || "",
          registrationNumber: backendData.registrationNumber || ""
        };
        const result = await PaymentService.createDomainPaymentIntent(domainData);
        return result;
      }
      throw new Error("Payment intent creation not supported for this payment type");
    } catch (error) {
      throw error;
    }
  };
  const createSubscription = async (backendData, paymentMethodId) => {
    try {
      if (onCreateSubscription) {
        return await onCreateSubscription(backendData, paymentMethodId);
      }
      const subscriptionTier = normalizeCheckoutSubscriptionTier(paymentDetails);
      const generatedPlanId = generatePlanId(subscriptionTier);
      const projectIdForPayload = backendData.projectId || generatedPlanId;
      const subscriptionData = {
        planId: `${subscriptionTier.toLowerCase()}_monthly`,
        paymentMethodId: paymentMethodId,
        customerEmail: backendData.emailAddress,
        customerName: backendData.fullName,
        productName: backendData.productName || paymentDetails.productName || "EIS Service",
        product: backendData.productName || paymentDetails.productName || "EIS Service",
        projectId: projectIdForPayload,
        projectTier: subscriptionTier,
        subscriptionPlan: subscriptionTier,
        fullName: backendData.fullName,
        emailAddress: backendData.emailAddress,
        billingAddress: backendData.billingAddress,
        city: backendData.city,
        stateRegion: backendData.stateRegion,
        country: backendData.country,
        phone: backendData.phone,
        amount: paymentDetails.amount,
        currency: paymentDetails.currency || user?.currency || "RON",
        websiteType: "Business / Corporate Website",
        status: "active",
        autoRenew: true,
        isUpdating: paymentDetails.isManagingMode === "true" && String(paymentDetails?.status || "").toLowerCase() !== "canceled",
        existingPlanId: paymentDetails.existingPlanId,
        existingStripeSubscriptionId: paymentDetails.isManagingMode === "true" && String(paymentDetails?.status || "").toLowerCase() !== "canceled" ? paymentDetails.existingStripeSubscriptionId : ""
      };
      const result = await createSubscriptionWithStripe(subscriptionData);
      if (!result.success) {
        throw new Error(result.error || "Failed to create subscription");
      }
      return result.data;
    } catch (error) {
      throw error;
    }
  };
  const handleSubmit = async event => {
    event.preventDefault();
    const stEarly = String(paymentDetails?.status || "").toLowerCase();
    const managingEarly = paymentDetails?.isManagingMode === true || paymentDetails?.isManagingMode === "true";
    const subIdEarly = paymentDetails?.existingStripeSubscriptionId || paymentDetails?.existingStripeSubscriptionID || paymentDetails?.stripeSubscriptionId;
    if (paymentDetails?.isSubscription && managingEarly && subIdEarly && ["active", "paid", "trialing", "canceling"].includes(stEarly)) {
      return;
    }
    if (!stripe || !elements) {
      return;
    }
    if (!validateForm()) {
      return;
    }
    setProcessing(true);
    try {
      const isSubscription = paymentDetails?.isSubscription;
      const finalProductName = paymentDetails?.productName || paymentDetails?.existingProductName || productInfo.productName || (isSubscription ? "EIS Service" : "") || "";
      if (!finalProductName || finalProductName.trim() === "") {
        throw new Error("Product name is required and cannot be empty");
      }
      const subscriptionTier = normalizeCheckoutSubscriptionTier(paymentDetails);
      const generatedPlanId = isSubscription ? generatePlanId(subscriptionTier) : paymentDetails?.planId || "default-project-id";
      const stableSubscriptionProjectId = isSubscription && (paymentDetails?.isManagingMode === true || paymentDetails?.isManagingMode === "true") && paymentDetails?.projectId ? paymentDetails.projectId : generatedPlanId;
      let billingInfoToUse = cardHolderInfo;
      if (paymentMethod === "saved" && selectedPaymentMethodId) {
        const selectedMethod = paymentMethods.find(method => method.id === selectedPaymentMethodId);
        if (selectedMethod?.billingInfo) {
          billingInfoToUse = {
            name: selectedMethod.billingInfo.fullName,
            email: selectedMethod.billingInfo.email,
            phone: selectedMethod.billingInfo.phone,
            address: {
              line1: selectedMethod.billingInfo.address?.street,
              city: selectedMethod.billingInfo.address?.city,
              state: selectedMethod.billingInfo.address?.state,
              country: selectedMethod.billingInfo.address?.country,
              postal_code: selectedMethod.billingInfo.address?.zip
            },
            companyAddress: {
              line1: selectedMethod.billingInfo.address?.street,
              city: selectedMethod.billingInfo.address?.city,
              state: selectedMethod.billingInfo.address?.state,
              country: selectedMethod.billingInfo.address?.country,
              postal_code: selectedMethod.billingInfo.address?.zip
            },
            companyName: selectedMethod.billingInfo.companyName || "",
            vatNumber: selectedMethod.billingInfo.vatNumber || "",
            registrationNumber: selectedMethod.billingInfo.registrationNumber || ""
          };
        }
      }
      const stripeBillingDetails = {
        name: customerType === "company" ? billingInfoToUse.companyName : billingInfoToUse.name,
        email: billingInfoToUse.email,
        address: customerType === "company" ? billingInfoToUse.companyAddress : billingInfoToUse.address,
        phone: billingInfoToUse.phone
      };
      const backendData = {
        projectId: stableSubscriptionProjectId,
        fullName: customerType === "company" ? billingInfoToUse.companyName || user?.name || "Customer" : billingInfoToUse.name || user?.name || "Customer",
        emailAddress: billingInfoToUse.email || user?.email || "",
        billingAddress: customerType === "company" ? billingInfoToUse.companyAddress?.line1 || "Default Address" : billingInfoToUse.address?.line1 || "Default Address",
        city: customerType === "company" ? billingInfoToUse.companyAddress?.city || "Default City" : billingInfoToUse.address?.city || "Default City",
        stateRegion: customerType === "company" ? billingInfoToUse.companyAddress?.state || "" : billingInfoToUse.address?.state || "",
        country: customerType === "company" ? billingInfoToUse.companyAddress?.country || "US" : billingInfoToUse.address?.country || "US",
        zipCode: customerType === "company" ? billingInfoToUse.companyAddress?.postal_code || "" : billingInfoToUse.address?.postal_code || "",
        phone: billingInfoToUse.phone || "",
        product: finalProductName.trim(),
        planName: paymentDetails.planName,
        productName: finalProductName.trim(),
        existingProductName: paymentDetails.existingProductName,
        billingCycle: paymentDetails.billingCycle,
        planTier: subscriptionTier,
        customerType: customerType,
        contactName: billingInfoToUse.name || user?.name || "Customer",
        companyName: billingInfoToUse.companyName || "",
        vatNumber: billingInfoToUse.vatNumber || "",
        registrationNumber: billingInfoToUse.registrationNumber || ""
      };
      if (isSubscription) {
        if (paymentMethod === "saved" && selectedPaymentMethodId) {
          const result = await createSubscription(backendData, selectedPaymentMethodId);
          onPaymentSuccess(result);
        } else {
          const cardElement = elements.getElement(CardElement);
          const {
            error: pmError,
            paymentMethod: newPaymentMethod
          } = await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
            billing_details: stripeBillingDetails
          });
          if (pmError) {
            onPaymentError(pmError);
            return;
          }
          const result = await createSubscription(backendData, newPaymentMethod.id);
          onPaymentSuccess(result);
        }
      } else {
        if (!currentPaymentIntent) {
          const newPaymentIntent = await createPaymentIntent(backendData);
          setCurrentPaymentIntent(newPaymentIntent);
          if (paymentMethod === "saved" && selectedPaymentMethodId) {
            const {
              error
            } = await stripe.confirmCardPayment(newPaymentIntent.clientSecret, {
              payment_method: selectedPaymentMethodId
            });
            if (error) {
              onPaymentError(error);
            } else {
              const {
                paymentIntent: confirmedPaymentIntent
              } = await stripe.retrievePaymentIntent(newPaymentIntent.clientSecret);
              onPaymentSuccess(confirmedPaymentIntent);
            }
          } else {
            const cardElement = elements.getElement(CardElement);
            const {
              error,
              paymentIntent: confirmedPaymentIntent
            } = await stripe.confirmCardPayment(newPaymentIntent.clientSecret, {
              payment_method: {
                card: cardElement,
                billing_details: stripeBillingDetails
              }
            });
            if (error) {
              onPaymentError(error);
            } else {
              onPaymentSuccess(confirmedPaymentIntent);
            }
          }
        } else {
          if (paymentMethod === "saved" && selectedPaymentMethodId) {
            const {
              error
            } = await stripe.confirmCardPayment(currentPaymentIntent.clientSecret, {
              payment_method: selectedPaymentMethodId
            });
            if (error) {
              onPaymentError(error);
            } else {
              const {
                paymentIntent: confirmedPaymentIntent
              } = await stripe.retrievePaymentIntent(currentPaymentIntent.clientSecret);
              onPaymentSuccess(confirmedPaymentIntent);
            }
          } else {
            const cardElement = elements.getElement(CardElement);
            const {
              error,
              paymentIntent: confirmedPaymentIntent
            } = await stripe.confirmCardPayment(currentPaymentIntent.clientSecret, {
              payment_method: {
                card: cardElement,
                billing_details: stripeBillingDetails
              }
            });
            if (error) {
              onPaymentError(error);
            } else {
              onPaymentSuccess(confirmedPaymentIntent);
            }
          }
        }
      }
    } catch (error) {
      onPaymentError(error);
    } finally {
      setProcessing(false);
    }
  };
  const getCardIcon = brand => {
    switch (brand) {
      case "visa":
        return "💳";
      case "mastercard":
        return "🟠🔴";
      case "amex":
        return "💳";
      default:
        return "💳";
    }
  };
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      window.history.back();
    }
  };
  const handleConfirmCancelSubscription = async ({
    reason,
    feedback,
    cancelImmediately
  }) => {
    const subscriptionId = paymentDetails?.existingStripeSubscriptionId || paymentDetails?.existingStripeSubscriptionID || paymentDetails?.stripeSubscriptionId;
    if (!subscriptionId) {
      setShowCancelSubscriptionPanel(false);
      return;
    }
    setIsCancelProcessing(true);
    setActionError("");
    try {
      const existingPlanId = paymentDetails?.existingPlanId || undefined;
      let response = await cancelSubscriptionWithStripe({
        subscriptionId,
        existingPlanId,
        reason: reason || "User requested cancellation",
        feedback: feedback || "",
        cancelImmediately: Boolean(cancelImmediately)
      });
      if (!response?.success && response?.code === "STALE_SUBSCRIPTION_REFERENCE" && existingPlanId) {
        const refresh = await getCompleteSubscriptionData(existingPlanId);
        if (refresh?.success && refresh?.data?.stripeSubscriptionId) {
          response = await cancelSubscriptionWithStripe({
            subscriptionId: refresh.data.stripeSubscriptionId,
            existingPlanId,
            reason: reason || "User requested cancellation",
            feedback: feedback || "",
            cancelImmediately: Boolean(cancelImmediately)
          });
        }
      }
      if (!response?.success) {
        throw new Error(response?.error || "cancel_failed");
      }
      setShowCancelSubscriptionPanel(false);
      window.location.href = "/billing";
    } catch (e) {
      logClientError("checkout.cancelSubscription", e);
      setActionError(t("checkoutPage.actionError"));
    } finally {
      setIsCancelProcessing(false);
    }
  };
  const handleKeepSubscription = () => {
    setShowCancelSubscriptionPanel(false);
    setCancelReason("");
    setCancelFeedback("");
  };
  const handleConfirmCancelInline = () => {
    handleConfirmCancelSubscription({
      reason: cancelReason,
      feedback: cancelFeedback,
      cancelImmediately: false
    });
  };
  const handleOpenCancelModal = () => {
    setShowCancelSubscriptionPanel(true);
  };
  const handleCloseCancelModal = () => {
    handleKeepSubscription();
  };
  const handleSetDefaultPaymentMethod = async () => {
    if (!selectedPaymentMethodId || typeof onRefreshPaymentMethods !== "function") {
      return;
    }
    const selected = paymentMethods.find(m => m.id === selectedPaymentMethodId);
    if (selected?.isDefault) return;
    setSettingDefaultPm(true);
    setActionError("");
    try {
      const data = await PaymentService.setDefaultPaymentMethod(selectedPaymentMethodId);
      if (data && data.success === false) {
        throw new Error(data.message || "set_default_failed");
      }
      try {
        localStorage.setItem("defaultPaymentMethod", selectedPaymentMethodId);
      } catch {}
      await onRefreshPaymentMethods();
    } catch (e) {
      logClientError("checkout.setDefaultPaymentMethod", e);
      setActionError(t("checkoutPage.actionError"));
    } finally {
      setSettingDefaultPm(false);
    }
  };
  const handleResumeRenewal = async () => {
    const subId = paymentDetails?.existingStripeSubscriptionId || paymentDetails?.existingStripeSubscriptionID || paymentDetails?.stripeSubscriptionId;
    if (!subId) return;
    setIsResumeProcessing(true);
    setActionError("");
    try {
      let response = await resumeSubscriptionRenewal({
        subscriptionId: subId
      });
      if (!response.success && paymentDetails?.existingPlanId) {
        const refresh = await getCompleteSubscriptionData(paymentDetails.existingPlanId);
        if (refresh?.success && refresh?.data?.stripeSubscriptionId) {
          response = await resumeSubscriptionRenewal({
            subscriptionId: refresh.data.stripeSubscriptionId
          });
        }
      }
      if (!response.success) {
        throw new Error(response?.error || "resume_failed");
      }
      navigate("/billing");
    } catch (e) {
      logClientError("checkout.resumeRenewal", e);
      setActionError(t("checkoutPage.actionError"));
    } finally {
      setIsResumeProcessing(false);
    }
  };
  const isPortalLikeLayout = true;
  const isSubscription = Boolean(paymentDetails?.isSubscription);
  const isDomainExtension = paymentDetails?.type === "domain_extension";
  const isManagingMode = paymentDetails?.isManagingMode === true || paymentDetails?.isManagingMode === "true";
  const subscriptionStatus = String(paymentDetails?.status || "").toLowerCase();
  const subscriptionId = paymentDetails?.existingStripeSubscriptionId || paymentDetails?.existingStripeSubscriptionID || paymentDetails?.stripeSubscriptionId;
  const canUseManageSubscriptionView = isSubscription && isManagingMode && Boolean(subscriptionId) && ["active", "paid", "trialing", "canceling"].includes(subscriptionStatus);
  const showManageSubscriptionOnly = canUseManageSubscriptionView;
  const isSubscriptionCanceling = subscriptionStatus === "canceling";
  const showCancelSubscriptionButton = canUseManageSubscriptionView && ["active", "paid", "trialing"].includes(subscriptionStatus);
  const showResumeRenewalButton = canUseManageSubscriptionView && isSubscriptionCanceling;
  const showPaymentMethodsInManageFlow = showManageSubscriptionOnly && showCancelSubscriptionButton;
  const manageStatusLabel = (() => {
    const s = subscriptionStatus;
    if (s === "canceling") return t("checkoutPage.portal.manageStatusCanceling");
    if (s === "trialing" || s === "active" || s === "paid") return t("checkoutPage.portal.manageStatusActive");
    return subscriptionStatus || "—";
  })();
  const renewalDateLabel = (() => {
    const raw = paymentDetails?.renewalDate;
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  })();
  const helpContextTitle = isSubscription ? t("checkoutPage.portal.helpPanelKickerSubscription") : t("checkoutPage.portal.helpPanelKickerPayment");
  const orderAmount = Number(paymentDetails?.amount ?? 0);
  const taxAmount = 0;
  const subtotalAmount = orderAmount;
  const totalAmount = subtotalAmount + taxAmount;
  const fmtMoney = n => `${getCurrencySymbol(paymentDetails?.currency)} ${Number(n || 0).toFixed(2)}`;
  const orderProductName = paymentDetails?.existingProductName || paymentDetails?.productName || productInfo.productName || "";
  const orderHeadline = isDomainExtension ? paymentDetails?.domain || "—" : orderProductName || (isSubscription ? `${normalizeCheckoutSubscriptionTier(paymentDetails)} subscription` : paymentDetails?.description || "—");
  const planTierStr = normalizeCheckoutSubscriptionTier(paymentDetails);
  const planDisplayName = isSubscription ? `${planTierStr} Plan` : "";
  const rawBillingCycle = String(paymentDetails?.billingCycle || "").trim().toLowerCase();
  const billingScheduleLabel = !rawBillingCycle && isSubscription ? t("checkoutPage.portal.billingMonthly") : !rawBillingCycle ? t("checkoutPage.portal.oneTime") : rawBillingCycle.includes("year") ? t("checkoutPage.portal.billingYearly") : rawBillingCycle.includes("month") ? t("checkoutPage.portal.billingMonthly") : rawBillingCycle.includes("week") ? t("checkoutPage.portal.billingWeekly") : rawBillingCycle.includes("day") ? t("checkoutPage.portal.billingDaily") : String(paymentDetails?.billingCycle || "").trim() || "—";
  const orderDetailRows = (() => {
    if (isDomainExtension) {
      const rows = [{
        key: "type",
        label: t("checkoutPage.portal.lineType"),
        value: t("checkoutPage.portal.typeDomain")
      }, {
        key: "domain",
        label: t("checkoutPage.portal.lineDomain"),
        value: paymentDetails?.domain || "—"
      }];
      const y = Number(paymentDetails?.years);
      if (y > 0) {
        rows.push({
          key: "period",
          label: t("checkoutPage.portal.linePeriod"),
          value: y === 1 ? t("checkoutPage.portal.periodOneYear") : t("checkoutPage.portal.periodNYears", {
            count: y
          })
        });
      }
      const desc = String(paymentDetails?.description || "").trim();
      if (desc) {
        rows.push({
          key: "service",
          label: t("checkoutPage.portal.lineService"),
          value: desc
        });
      }
      return rows;
    }
    if (isSubscription) {
      return [{
        key: "type",
        label: t("checkoutPage.portal.lineType"),
        value: t("checkoutPage.portal.typeSubscription")
      }, {
        key: "product",
        label: t("checkoutPage.portal.lineProduct"),
        value: orderProductName || "—"
      }, {
        key: "plan",
        label: t("checkoutPage.portal.linePlan"),
        value: planDisplayName || "—",
        badge: true,
        planTier: planTierStr
      }, {
        key: "billing",
        label: t("checkoutPage.portal.lineBilling"),
        value: billingScheduleLabel
      }];
    }
    const rows = [{
      key: "type",
      label: t("checkoutPage.portal.lineType"),
      value: t("checkoutPage.portal.typeOneTime")
    }];
    if (orderProductName) {
      rows.push({
        key: "product",
        label: t("checkoutPage.portal.lineProduct"),
        value: orderProductName
      });
    }
    const desc = String(paymentDetails?.description || "").trim();
    if (desc) {
      rows.push({
        key: "details",
        label: t("checkoutPage.portal.lineDetails"),
        value: desc
      });
    }
    return rows;
  })();
  return (
    <div className={styles.checkoutContainer}>
      <div className={styles.shell}>
        <aside className={styles.helpPanel} aria-label="Help panel">
          <div className={styles.helpTop}>
            <img className={styles.helpLogo} src={softindexLogo} alt="Softindex" loading="eager" />
            <div className={styles.helpTopKicker}>{helpContextTitle}</div>
            {showManageSubscriptionOnly ? <p className={styles.helpIntro}>
                {t("checkoutPage.portal.manageActivePageIntro")}
              </p> : null}
          </div>
          <div className={styles.helpBody}>
            <div className={styles.helpTitle}>
              {t("checkoutPage.helpPanel.needHelpTitle")}
            </div>
            <div className={styles.helpText}>
              <Trans t={t} i18nKey="checkoutPage.helpPanel.needHelpBody" components={{
              supportLink: (
                <a
                  className={styles.helpLink}
                  href="https://example.com/support"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Support"
                />
              ),
              emailLink: (
                <a
                  className={styles.helpLink}
                  href="mailto:support@softindex.ro"
                  aria-label="Email support"
                />
              )
            }} />
            </div>
            <button className={styles.helpBack} type="button" onClick={handleCancel}>
              {t("checkoutPage.helpPanel.backButton")}
            </button>
          </div>
        </aside>

        <main className={styles.main}>
          {paymentMethodsListError ? (
            <div className={styles.inlineBanner} role="alert">
              {paymentMethodsListError}
            </div>
          ) : null}
          {actionError ? (
            <div className={styles.inlineBanner} role="alert">
              {actionError}
            </div>
          ) : null}
          <header className={styles.portalHeader}>
            <h1 className={styles.portalTitle}>
              {showManageSubscriptionOnly ? t("checkoutPage.portal.manageActivePageTitle") : t("checkoutPage.title")}
            </h1>
          </header>

          <form onSubmit={handleSubmit} className={styles.portalStack} autoComplete="off">
            <section className={styles.portalCard}>
              <div className={styles.cardHeaderRow}>
                <div>
                  <div className={styles.cardEyebrow}>
                    {showManageSubscriptionOnly ? t("checkoutPage.portal.manageActiveEyebrow") : t("checkoutPage.portal.orderEyebrow")}
                  </div>
                  <div className={styles.cardTitle}>{orderHeadline}</div>
                  <p className={styles.cardSubtitle}>
                    {showManageSubscriptionOnly ? isSubscriptionCanceling ? t("checkoutPage.portal.manageActiveOrderSubtitleCanceling") : t("checkoutPage.portal.manageActiveOrderSubtitle") : isDomainExtension ? t("checkoutPage.portal.orderSubtitleDomain") : isSubscription ? t("checkoutPage.portal.orderSubtitleSubscription") : t("checkoutPage.portal.orderSubtitleOneTime")}
                  </p>
                </div>
                <div className={styles.cardRight}>
                  {showResumeRenewalButton ? <button type="button" className={styles.resumeRenewalBtn} onClick={handleResumeRenewal} disabled={isResumeProcessing}>
                      {isResumeProcessing ? t("checkoutPage.paymentMethod.processing") : t("billingPage.subscriptionManage.resumeRenewal")}
                    </button> : showCancelSubscriptionButton ? <button type="button" className={styles.secondaryBtn} onClick={handleOpenCancelModal}>
                      {t("checkoutPage.portal.cancelSubscriptionOpen")}
                    </button> : <button type="button" className={styles.secondaryBtn} onClick={handleCancel}>
                      {t("checkoutPage.backButton")}
                    </button>}
                </div>
              </div>

              <div className={styles.orderDetailList}>
                {orderDetailRows.map(row => <div key={row.key} className={styles.orderDetailRow}>
                    <span className={styles.orderDetailLabel}>{row.label}</span>
                    <div className={styles.orderDetailValueCell}>
                      {row.badge ? <span className={`${styles.planBadge} ${styles[`plan${row.planTier}`] || ""}`}>
                          {row.value}
                        </span> : <span className={styles.orderDetailValue}>{row.value}</span>}
                    </div>
                  </div>)}
                {showManageSubscriptionOnly && paymentDetails?.projectId && <div className={styles.orderDetailRow}>
                    <span className={styles.orderDetailLabel}>
                      {t("checkoutPage.portal.lineProjectId")}
                    </span>
                    <div className={styles.orderDetailValueCell}>
                      <span className={styles.orderDetailValue}>
                        {paymentDetails.projectId}
                      </span>
                    </div>
                  </div>}
              </div>

              {showCancelSubscriptionButton && showCancelSubscriptionPanel && <div className={styles.cancelModalOverlay} onClick={e => {
              if (e.target === e.currentTarget && !isCancelProcessing) {
                handleCloseCancelModal();
              }
            }} role="dialog" aria-modal="true" aria-labelledby="cancel-subscription-dialog-title">
                  <div className={styles.cancelModalContent} onClick={e => e.stopPropagation()}>
                    <div className={styles.cancelModalHeader}>
                      <h2 id="cancel-subscription-dialog-title" className={styles.cancelModalTitle}>
                        {t("checkoutPage.portal.cancelSubscriptionOpen")}
                      </h2>
                      <button type="button" className={styles.cancelModalClose} onClick={handleCloseCancelModal} disabled={isCancelProcessing} aria-label={t("checkoutPage.portal.cancelSubscriptionClose")} title={t("checkoutPage.portal.cancelSubscriptionClose")}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>

                    <div className={styles.cancelModalBody}>
                      <div className={styles.cancelModalInner}>
                        <div className={styles.cancelModalHero}>
                          <div className={`${styles.cancelModalHeroFrame} ${styles.cancelModalHeroFrameWarning}`}>
                            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden>
                              <circle cx="12" cy="12" r="10" stroke="#b45309" strokeWidth="2" fill="#fffbeb" />
                              <path d="M12 8v4M12 16h.01" stroke="#b45309" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                          </div>
                        </div>
                        <p className={styles.cancelIntro}>
                          {t("checkoutPage.portal.cancelSubscriptionIntro")}
                        </p>
                        <div className={styles.cancelModalFormCard}>
                          <div className={styles.cancelField}>
                            <label className={styles.cancelLabel} htmlFor="cancel-subscription-reason">
                              {t("checkoutPage.portal.cancelReasonLabel")}
                            </label>
                            <select id="cancel-subscription-reason" className={styles.cancelSelect} value={cancelReason} onChange={e => setCancelReason(e.target.value)} disabled={isCancelProcessing}>
                              <option value="">
                                {t("checkoutPage.portal.cancelReasonSelect")}
                              </option>
                              <option value="too_expensive">
                                {t("checkoutPage.portal.cancelReasonTooExpensive")}
                              </option>
                              <option value="not_using">
                                {t("checkoutPage.portal.cancelReasonNotUsing")}
                              </option>
                              <option value="missing_features">
                                {t("checkoutPage.portal.cancelReasonMissingFeatures")}
                              </option>
                              <option value="switching_provider">
                                {t("checkoutPage.portal.cancelReasonSwitching")}
                              </option>
                              <option value="temporary_pause">
                                {t("checkoutPage.portal.cancelReasonTemporary")}
                              </option>
                              <option value="other">
                                {t("checkoutPage.portal.cancelReasonOther")}
                              </option>
                            </select>
                          </div>
                          <div className={styles.cancelField}>
                            <label className={styles.cancelLabel} htmlFor="cancel-subscription-feedback">
                              {t("checkoutPage.portal.cancelFeedbackLabel")}
                            </label>
                            <textarea id="cancel-subscription-feedback" className={styles.cancelTextarea} rows={3} value={cancelFeedback} onChange={e => setCancelFeedback(e.target.value)} disabled={isCancelProcessing} placeholder={t("checkoutPage.portal.cancelFeedbackPlaceholder")} />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={styles.cancelModalFooter}>
                      <button type="button" className={styles.cancelModalBtnSecondary} onClick={handleCloseCancelModal} disabled={isCancelProcessing}>
                        {t("checkoutPage.portal.keepSubscription")}
                      </button>
                      <button type="button" className={styles.cancelModalBtnDanger} onClick={handleConfirmCancelInline} disabled={isCancelProcessing}>
                        {isCancelProcessing ? t("checkoutPage.paymentMethod.processing") : t("checkoutPage.portal.confirmCancelSubscription")}
                      </button>
                    </div>
                  </div>
                </div>}
            </section>

            {(!showManageSubscriptionOnly || showPaymentMethodsInManageFlow) && <section className={styles.portalCard}>
              <div className={styles.cardHeaderRow}>
                <div>
                  <div className={styles.cardEyebrow}>PAYMENT</div>
                  <div className={styles.cardTitle}>
                    {showPaymentMethodsInManageFlow ? t("checkoutPage.portal.managePaymentTitle") : t("checkoutPage.portal.paymentMethodsSectionTitle")}
                  </div>
                  {showPaymentMethodsInManageFlow && <p className={styles.cardSubtitle} style={{
                  marginTop: 8
                }}>
                      {t("checkoutPage.portal.managePaymentSubtitle")}
                    </p>}
                </div>
              </div>

              {paymentMethods && paymentMethods.length > 0 ? <div className={styles.pmList}>
                  {paymentMethods.map(method => <label key={method.id} className={styles.pmItem}>
                      <input type="radio" name="selectedCard" value={method.id} checked={selectedPaymentMethodId === method.id} onChange={e => {
                  setPaymentMethod("saved");
                  handlePaymentMethodChange(e.target.value);
                }} className={styles.pmRadio} />
                      <div className={styles.pmBody}>
                        <div className={styles.pmBrandRow}>
                          <span className={styles.pmIcon}>
                            {getCardIcon(method.brand)}
                          </span>
                          <span className={styles.pmBrand}>
                            {method.brand.charAt(0).toUpperCase() + method.brand.slice(1)}{" "}
                            •••• {method.last4}
                          </span>
                          {method.isDefault && <span className={styles.pmBadge}>Default</span>}
                        </div>
                        <div className={styles.pmHint}>
                          Expires {method.expiryMonth}/{method.expiryYear}
                        </div>
                      </div>
                    </label>)}
                  {errors.paymentMethod && <div className={styles.errorMessage}>{errors.paymentMethod}</div>}
                  {showPaymentMethodsInManageFlow && <div className={styles.pmManageActions}>
                      <button type="button" className={styles.setDefaultPmBtn} onClick={handleSetDefaultPaymentMethod} disabled={settingDefaultPm || !selectedPaymentMethodId || paymentMethods.find(m => m.id === selectedPaymentMethodId)?.isDefault}>
                        {settingDefaultPm ? t("checkoutPage.paymentMethod.processing") : t("checkoutPage.portal.setDefaultPaymentMethod")}
                      </button>
                    </div>}
                  {!showPaymentMethodsInManageFlow && <div className={styles.pmAddRow}>
                      <button type="button" className={styles.linkBtn} onClick={() => setPaymentMethod("new")}>
                        + Add payment method
                      </button>
                    </div>}
                </div> : <div className={styles.pmEmpty}>
                  <div className={styles.pmEmptyTitle}>
                    {t("checkoutPage.portal.pmEmptyTitle")}
                  </div>
                  <p className={styles.pmEmptyHint}>
                    {showPaymentMethodsInManageFlow ? t("checkoutPage.portal.managePmEmptyHint") : t("checkoutPage.portal.pmEmptyHint")}
                  </p>
                </div>}

              {paymentMethod === "new" && !showPaymentMethodsInManageFlow && <div className={styles.newCardBlock}>
                  <div className={styles.sectionTitle}>
                    {t("checkoutPage.paymentMethod.cardDetails")}
                  </div>
                  <CardElement className={styles.cardElement} />
                </div>}
            </section>}

            {!showManageSubscriptionOnly && <section className={styles.portalCard}>
              <div className={styles.cardHeaderRow}>
                <div>
                  <div className={styles.cardEyebrow}>BILLING</div>
                  <div className={styles.cardTitle}>Information</div>
                </div>
              </div>

              {(paymentDetails?.requiresBillingInfo || paymentMethod === "new") && !currentPaymentIntent && <div className={styles.billingSection}>
                  <div className={styles.cardHolderSection}>
                    <h3 className={styles.subsectionTitle}>
                      {t("checkoutPage.billingInformation.title")}
                    </h3>

                    <div className={styles.customerTypeSection}>
                      <h4 className={styles.customerTypeTitle}>
                        {t("checkoutPage.billingInformation.customerType")}
                      </h4>
                      <div className={styles.customerTypeContainer}>
                        <div className={styles.customerTypeOption}>
                          <input type="radio" id="person" name="customerType" value="person" checked={customerType === "person"} onChange={e => handleCustomerTypeChange(e.target.value)} className={styles.radioInput} />
                          <label htmlFor="person" className={styles.radioLabel}>
                            <span className={styles.radioIcon}>👤</span>
                            <div>
                              <div className={styles.radioTitle}>
                                {t("checkoutPage.billingInformation.person")}
                              </div>
                              <div className={styles.radioDescription}>
                                Individual billing
                              </div>
                            </div>
                          </label>
                        </div>
                        <div className={styles.customerTypeOption}>
                          <input type="radio" id="company" name="customerType" value="company" checked={customerType === "company"} onChange={e => handleCustomerTypeChange(e.target.value)} className={styles.radioInput} />
                          <label htmlFor="company" className={styles.radioLabel}>
                            <span className={styles.radioIcon}>🏢</span>
                            <div>
                              <div className={styles.radioTitle}>
                                {t("checkoutPage.billingInformation.company")}
                              </div>
                              <div className={styles.radioDescription}>
                                Business billing with VAT
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className={styles.formRow}>
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                          {t("checkoutPage.billingInformation.name")}{" "}
                          <span className={styles.required}>*</span>
                        </label>
                        <input type="text" className={`${styles.formInput} ${errors.cardHolderName ? styles.inputError : ""}`} value={cardHolderInfo.name} onChange={e => handleCardHolderChange("name", e.target.value)} placeholder="John Doe" autocomplete="off" />
                        {errors.cardHolderName && <span className={styles.errorMessage}>
                            {errors.cardHolderName}
                          </span>}
                      </div>

                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                          {t("checkoutPage.billingInformation.email")}{" "}
                          <span className={styles.required}>*</span>
                        </label>
                        <input type="email" className={`${styles.formInput} ${errors.email ? styles.inputError : ""}`} value={cardHolderInfo.email} onChange={e => handleCardHolderChange("email", e.target.value)} placeholder="john@example.com" autocomplete="off" />
                        {errors.email && <span className={styles.errorMessage}>
                            {errors.email}
                          </span>}
                      </div>
                    </div>

                    {customerType === "company" && <>
                        <div className={styles.formGroup}>
                          <label className={styles.formLabel}>
                            {t("checkoutPage.billingInformation.companyName")}{" "}
                            <span className={styles.required}>*</span>
                          </label>
                          <input type="text" className={`${styles.formInput} ${errors.companyName ? styles.inputError : ""}`} value={cardHolderInfo.companyName} onChange={e => handleCardHolderChange("companyName", e.target.value)} placeholder="Company Name" autocomplete="off" />
                          {errors.companyName && <span className={styles.errorMessage}>
                              {errors.companyName}
                            </span>}
                        </div>

                        <div className={styles.formRow}>
                          <div className={styles.formGroup}>
                            <label className={styles.formLabel}>
                              {t("checkoutPage.billingInformation.vatNumber")}{" "}
                              <span className={styles.required}>*</span>
                            </label>
                            <input type="text" className={`${styles.formInput} ${errors.vatNumber ? styles.inputError : ""}`} value={cardHolderInfo.vatNumber} onChange={e => handleCardHolderChange("vatNumber", e.target.value)} placeholder="VAT Number" autocomplete="off" />
                            {errors.vatNumber && <span className={styles.errorMessage}>
                                {errors.vatNumber}
                              </span>}
                          </div>

                          <div className={styles.formGroup}>
                            <label className={styles.formLabel}>
                              {t("checkoutPage.billingInformation.registrationNumber")}{" "}
                              <span className={styles.required}>*</span>
                            </label>
                            <input type="text" className={`${styles.formInput} ${errors.registrationNumber ? styles.inputError : ""}`} value={cardHolderInfo.registrationNumber} onChange={e => handleCardHolderChange("registrationNumber", e.target.value)} placeholder="Registration Number" autocomplete="off" />
                            {errors.registrationNumber && <span className={styles.errorMessage}>
                                {errors.registrationNumber}
                              </span>}
                          </div>
                        </div>
                      </>}

                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>
                        {t("checkoutPage.billingInformation.address")}{" "}
                        <span className={styles.required}>*</span>
                      </label>
                      <input type="text" className={`${styles.formInput} ${errors[customerType === "company" ? "companyAddress.line1" : "address.line1"] ? styles.inputError : ""}`} value={customerType === "company" ? cardHolderInfo.companyAddress.line1 : cardHolderInfo.address.line1} onChange={e => handleCardHolderChange(customerType === "company" ? "companyAddress.line1" : "address.line1", e.target.value)} placeholder="123 Main St" autocomplete="off" />
                      {errors[customerType === "company" ? "companyAddress.line1" : "address.line1"] && <span className={styles.errorMessage}>
                          {errors[customerType === "company" ? "companyAddress.line1" : "address.line1"]}
                        </span>}
                    </div>

                    <div className={styles.formRow}>
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                          {t("checkoutPage.billingInformation.city")}{" "}
                          <span className={styles.required}>*</span>
                        </label>
                        <input type="text" className={`${styles.formInput} ${errors[customerType === "company" ? "companyAddress.city" : "address.city"] ? styles.inputError : ""}`} value={customerType === "company" ? cardHolderInfo.companyAddress.city : cardHolderInfo.address.city} onChange={e => handleCardHolderChange(customerType === "company" ? "companyAddress.city" : "address.city", e.target.value)} placeholder="Anytown" autocomplete="off" />
                        {errors[customerType === "company" ? "companyAddress.city" : "address.city"] && <span className={styles.errorMessage}>
                            {errors[customerType === "company" ? "companyAddress.city" : "address.city"]}
                          </span>}
                      </div>

                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                          {t("checkoutPage.billingInformation.state")}
                        </label>
                        <input type="text" className={styles.formInput} value={customerType === "company" ? cardHolderInfo.companyAddress.state : cardHolderInfo.address.state} onChange={e => handleCardHolderChange(customerType === "company" ? "companyAddress.state" : "address.state", e.target.value)} placeholder="Anystate" autocomplete="off" />
                      </div>
                    </div>

                    <div className={styles.formRow}>
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                          {t("checkoutPage.billingInformation.country")}{" "}
                          <span className={styles.required}>*</span>
                        </label>
                        <select className={`${styles.formInput} ${errors[customerType === "company" ? "companyAddress.country" : "address.country"] ? styles.inputError : ""}`} value={customerType === "company" ? cardHolderInfo.companyAddress.country : cardHolderInfo.address.country} onChange={e => handleCardHolderChange(customerType === "company" ? "companyAddress.country" : "address.country", e.target.value)}>
                          <option value="">Select Country</option>
                          {countries.map(country => <option key={country.code} value={country.code}>
                              {country.name}
                            </option>)}
                        </select>
                        {errors[customerType === "company" ? "companyAddress.country" : "address.country"] && <span className={styles.errorMessage}>
                            {errors[customerType === "company" ? "companyAddress.country" : "address.country"]}
                          </span>}
                      </div>

                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                          ZIP <span className={styles.required}>*</span>
                        </label>
                        <input type="text" className={`${styles.formInput} ${errors[customerType === "company" ? "companyAddress.postal_code" : "address.postal_code"] ? styles.inputError : ""}`} value={customerType === "company" ? cardHolderInfo.companyAddress.postal_code : cardHolderInfo.address.postal_code} onChange={e => handleCardHolderChange(customerType === "company" ? "companyAddress.postal_code" : "address.postal_code", e.target.value)} placeholder="12345" autocomplete="off" />
                        {errors[customerType === "company" ? "companyAddress.postal_code" : "address.postal_code"] && <span className={styles.errorMessage}>
                            {errors[customerType === "company" ? "companyAddress.postal_code" : "address.postal_code"]}
                          </span>}
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>
                        {t("checkoutPage.billingInformation.phone")}{" "}
                        <span className={styles.required}>*</span>
                      </label>
                      <input type="tel" className={`${styles.formInput} ${errors.phone ? styles.inputError : ""}`} value={cardHolderInfo.phone} onChange={e => handleCardHolderChange("phone", e.target.value)} placeholder="+1234567890" autocomplete="off" />
                      {errors.phone && <span className={styles.errorMessage}>{errors.phone}</span>}
                    </div>
                  </div>
                </div>}
            </section>}

            <section className={`${styles.portalCard} ${styles.summaryCard}`}>
              <div className={styles.cardHeaderRow}>
                <div>
                  <div className={styles.cardEyebrow}>
                    {showManageSubscriptionOnly ? t("checkoutPage.portal.manageActiveSummaryEyebrow") : t("checkoutPage.portal.summaryEyebrow")}
                  </div>
                  <div className={styles.cardTitle}>
                    {showManageSubscriptionOnly ? t("checkoutPage.portal.manageActiveSummaryTitle") : t("checkoutPage.portal.summaryTitle")}
                  </div>
                </div>
              </div>
              {showManageSubscriptionOnly ? <div className={styles.manageSummaryBody}>
                  <div className={styles.summaryRow}>
                    <span>{t("checkoutPage.portal.manageActiveStatus")}</span>
                    <span className={styles.manageSummaryEmphasis}>
                      {manageStatusLabel}
                    </span>
                  </div>
                  <div className={styles.summaryRow}>
                    <span>{t("checkoutPage.portal.manageActivePlanRate")}</span>
                    <span>{fmtMoney(orderAmount)}</span>
                  </div>
                  {renewalDateLabel && <div className={styles.summaryRow}>
                      <span>{t("checkoutPage.portal.manageActiveRenewal")}</span>
                      <span>{renewalDateLabel}</span>
                    </div>}
                  <p className={styles.manageSummaryHint}>
                    {t("checkoutPage.portal.manageActiveHint")}
                  </p>
                </div> : <>
                  <div className={styles.summaryRows}>
                    <div className={styles.summaryRow}>
                      <span>{t("checkoutPage.portal.subtotal")}</span>
                      <span>{fmtMoney(subtotalAmount)}</span>
                    </div>
                    <div className={styles.summaryRow}>
                      <span>{t("checkoutPage.portal.tax")}</span>
                      <span>{fmtMoney(taxAmount)}</span>
                    </div>
                    <p className={styles.summaryTaxNote}>
                      {t("checkoutPage.portal.taxNote")}
                    </p>
                    <div className={styles.summaryDivider} />
                    <div className={styles.summaryRowTotal}>
                      <span>{t("checkoutPage.portal.total")}</span>
                      <span>{fmtMoney(totalAmount)}</span>
                    </div>
                  </div>
                  <div className={styles.invoiceStub}>
                    <div className={styles.invoiceTitle}>Invoice history</div>
                    <div className={styles.invoiceHint}>
                      You can view invoices anytime in Billing &amp; Payments.
                    </div>
                  </div>
                </>}
            </section>

            <div className={styles.portalActions}>
              {showManageSubscriptionOnly ? <button type="button" className={styles.primaryBtn} onClick={() => navigate("/billing")}>
                  {t("checkoutPage.portal.manageActiveBackBilling")}
                </button> : <button type="submit" className={styles.primaryBtn} disabled={processing}>
                  {processing ? t("checkoutPage.paymentMethod.processing") : t("checkoutPage.paymentMethod.completePayment")}
                </button>}
            </div>

            {}
            {!isPortalLikeLayout && <div className={styles.paymentMethodSection}>
                <h2 className={styles.sectionTitle}>
                  {t("checkoutPage.paymentMethod.title")}
                </h2>

                {paymentMethods && paymentMethods.length > 0 && <div className={styles.paymentOption}>
                    <label className={styles.radioLabel}>
                      <input type="radio" name="paymentMethod" value="saved" checked={paymentMethod === "saved"} onChange={e => setPaymentMethod(e.target.value)} className={styles.radioInput} />
                      <span className={styles.radioText}>
                        {t("checkoutPage.paymentMethod.useSavedCard")}
                      </span>
                    </label>

                    {paymentMethod === "saved" && <div className={styles.savedCards}>
                        {paymentMethods.map(method => <label key={method.id} className={styles.cardOption}>
                            <input type="radio" name="selectedCard" value={method.id} checked={selectedPaymentMethodId === method.id} onChange={e => handlePaymentMethodChange(e.target.value)} className={styles.cardRadio} />
                            <div className={styles.cardInfo}>
                              <span className={styles.cardIcon}>
                                {getCardIcon(method.brand)}
                              </span>
                              <div className={styles.cardDetails}>
                                <div className={styles.cardBrand}>
                                  {method.brand.charAt(0).toUpperCase() + method.brand.slice(1)}{" "}
                                  •••• {method.last4}
                                </div>
                                <div className={styles.cardExpiry}>
                                  Expires {method.expiryMonth}/{method.expiryYear}
                                </div>
                              </div>
                              {method.isDefault && <span className={styles.defaultBadge}>Default</span>}
                            </div>
                          </label>)}
                        {errors.paymentMethod && <div className={styles.errorMessage}>
                            {errors.paymentMethod}
                          </div>}
                      </div>}
                  </div>}

                <div className={styles.paymentOption}>
                  <label className={styles.radioLabel}>
                    <input type="radio" name="paymentMethod" value="new" checked={paymentMethod === "new"} onChange={e => setPaymentMethod(e.target.value)} className={styles.radioInput} />
                    <span className={styles.radioText}>
                      {t("checkoutPage.paymentMethod.useNewCard")}
                    </span>
                  </label>

                  {paymentMethod === "new" && <div className={styles.newCardForm}>
                      {paymentDetails?.requiresBillingInfo && !currentPaymentIntent && <div className={styles.billingSection}>
                            <div className={styles.cardHolderSection}>
                              <h3 className={styles.subsectionTitle}>
                                {t("checkoutPage.billingInformation.title")}
                              </h3>

                          {}
                          <div className={styles.customerTypeSection}>
                            <h4 className={styles.customerTypeTitle}>
                              {t("checkoutPage.billingInformation.customerType")}
                            </h4>
                            <div className={styles.customerTypeContainer}>
                              <div className={styles.customerTypeOption}>
                                <input type="radio" id="person" name="customerType" value="person" checked={customerType === "person"} onChange={e => handleCustomerTypeChange(e.target.value)} className={styles.radioInput} />
                                <label htmlFor="person" className={styles.radioLabel}>
                                  <span className={styles.radioIcon}>👤</span>
                                  <div>
                                    <div className={styles.radioTitle}>
                                      {t("checkoutPage.billingInformation.person")}
                                    </div>
                                    <div className={styles.radioDescription}>
                                      Individual billing
                                    </div>
                                  </div>
                                </label>
                              </div>
                              <div className={styles.customerTypeOption}>
                                <input type="radio" id="company" name="customerType" value="company" checked={customerType === "company"} onChange={e => handleCustomerTypeChange(e.target.value)} className={styles.radioInput} />
                                <label htmlFor="company" className={styles.radioLabel}>
                                  <span className={styles.radioIcon}>🏢</span>
                                  <div>
                                    <div className={styles.radioTitle}>
                                      {t("checkoutPage.billingInformation.company")}
                                    </div>
                                    <div className={styles.radioDescription}>
                                      Business billing with VAT
                                    </div>
                                  </div>
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className={styles.formRow}>
                            <div className={styles.formGroup}>
                              <label className={styles.formLabel}>
                                {t("checkoutPage.billingInformation.name")}{" "}
                                <span className={styles.required}>*</span>
                              </label>
                              <input type="text" className={`${styles.formInput} ${errors.cardHolderName ? styles.inputError : ""}`} value={cardHolderInfo.name} onChange={e => handleCardHolderChange("name", e.target.value)} placeholder="John Doe" autocomplete="off" />
                              {errors.cardHolderName && <span className={styles.errorMessage}>
                                  {errors.cardHolderName}
                                </span>}
                            </div>

                            <div className={styles.formGroup}>
                              <label className={styles.formLabel}>
                                {t("checkoutPage.billingInformation.email")}{" "}
                                <span className={styles.required}>*</span>
                              </label>
                              <input type="email" className={`${styles.formInput} ${errors.email ? styles.inputError : ""}`} value={cardHolderInfo.email} onChange={e => handleCardHolderChange("email", e.target.value)} placeholder="john@example.com" autocomplete="off" />
                              {errors.email && <span className={styles.errorMessage}>
                                  {errors.email}
                                </span>}
                            </div>
                          </div>

                          {}
                          {customerType === "company" && <>
                              <div className={styles.formGroup}>
                                <label className={styles.formLabel}>
                                  {t("checkoutPage.billingInformation.companyName")}{" "}
                                  <span className={styles.required}>*</span>
                                </label>
                                <input type="text" className={`${styles.formInput} ${errors.companyName ? styles.inputError : ""}`} value={cardHolderInfo.companyName} onChange={e => handleCardHolderChange("companyName", e.target.value)} placeholder="Acme Corporation Ltd." autocomplete="off" />
                                {errors.companyName && <span className={styles.errorMessage}>
                                    {errors.companyName}
                                  </span>}
                              </div>

                              <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                  <label className={styles.formLabel}>
                                    {t("checkoutPage.billingInformation.vatNumber")}{" "}
                                    <span className={styles.required}>*</span>
                                  </label>
                                  <input type="text" className={`${styles.formInput} ${errors.vatNumber ? styles.inputError : ""}`} value={cardHolderInfo.vatNumber} onChange={e => handleCardHolderChange("vatNumber", e.target.value)} placeholder="RO12345678" autocomplete="off" />
                                  {errors.vatNumber && <span className={styles.errorMessage}>
                                      {errors.vatNumber}
                                    </span>}
                                </div>

                                <div className={styles.formGroup}>
                                  <label className={styles.formLabel}>
                                    {t("checkoutPage.billingInformation.registrationNumber")}{" "}
                                    <span className={styles.required}>*</span>
                                  </label>
                                  <input type="text" className={`${styles.formInput} ${errors.registrationNumber ? styles.inputError : ""}`} value={cardHolderInfo.registrationNumber} onChange={e => handleCardHolderChange("registrationNumber", e.target.value)} placeholder="J40/1234/2020" autocomplete="off" />
                                  {errors.registrationNumber && <span className={styles.errorMessage}>
                                      {errors.registrationNumber}
                                    </span>}
                                </div>
                              </div>
                            </>}

                          <div className={styles.formRow}>
                            <div className={styles.formGroup}>
                              <label className={styles.formLabel}>
                                {t("checkoutPage.billingInformation.address")}{" "}
                                <span className={styles.required}>*</span>
                              </label>
                              <input type="text" className={`${styles.formInput} ${errors[customerType === "company" ? "companyAddress.line1" : "address.line1"] ? styles.inputError : ""}`} value={customerType === "company" ? cardHolderInfo.companyAddress.line1 : cardHolderInfo.address.line1} onChange={e => handleCardHolderChange(customerType === "company" ? "companyAddress.line1" : "address.line1", e.target.value)} placeholder="123 Main St" autocomplete="off" />
                              {errors[customerType === "company" ? "companyAddress.line1" : "address.line1"] && <span className={styles.errorMessage}>
                                  {errors[customerType === "company" ? "companyAddress.line1" : "address.line1"]}
                                </span>}
                            </div>

                            <div className={styles.formGroup}>
                              <label className={styles.formLabel}>
                                {t("checkoutPage.billingInformation.city")}{" "}
                                <span className={styles.required}>*</span>
                              </label>
                              <input type="text" className={`${styles.formInput} ${errors[customerType === "company" ? "companyAddress.city" : "address.city"] ? styles.inputError : ""}`} value={customerType === "company" ? cardHolderInfo.companyAddress.city : cardHolderInfo.address.city} onChange={e => handleCardHolderChange(customerType === "company" ? "companyAddress.city" : "address.city", e.target.value)} placeholder="Anytown" autocomplete="off" />
                              {errors[customerType === "company" ? "companyAddress.city" : "address.city"] && <span className={styles.errorMessage}>
                                  {errors[customerType === "company" ? "companyAddress.city" : "address.city"]}
                                </span>}
                            </div>
                          </div>

                          <div className={styles.formRow}>
                            <div className={styles.formGroup}>
                              <label className={styles.formLabel}>
                                {t("checkoutPage.billingInformation.state")}
                              </label>
                              <input type="text" className={styles.formInput} value={customerType === "company" ? cardHolderInfo.companyAddress.state : cardHolderInfo.address.state} onChange={e => handleCardHolderChange(customerType === "company" ? "companyAddress.state" : "address.state", e.target.value)} placeholder="Anystate" autocomplete="off" />
                            </div>

                            <div className={styles.formGroup}>
                              <label className={styles.formLabel}>
                                {t("checkoutPage.billingInformation.country")}{" "}
                                <span className={styles.required}>*</span>
                              </label>
                              <select className={`${styles.formInput} ${errors[customerType === "company" ? "companyAddress.country" : "address.country"] ? styles.inputError : ""}`} value={customerType === "company" ? cardHolderInfo.companyAddress.country : cardHolderInfo.address.country} onChange={e => handleCardHolderChange(customerType === "company" ? "companyAddress.country" : "address.country", e.target.value)}>
                                <option value="">Select Country</option>
                                {countries.map(country => <option key={country.code} value={country.code}>
                                    {country.name}
                                  </option>)}
                              </select>
                              {errors[customerType === "company" ? "companyAddress.country" : "address.country"] && <span className={styles.errorMessage}>
                                  {errors[customerType === "company" ? "companyAddress.country" : "address.country"]}
                                </span>}
                            </div>
                          </div>

                          <div className={styles.formGroup}>
                            <label className={styles.formLabel}>
                              {t("checkoutPage.billingInformation.phone")}{" "}
                              <span className={styles.required}>*</span>
                            </label>
                            <input type="tel" className={`${styles.formInput} ${errors.phone ? styles.inputError : ""}`} value={cardHolderInfo.phone} onChange={e => handleCardHolderChange("phone", e.target.value)} placeholder="+1234567890" autocomplete="off" />
                            {errors.phone && <span className={styles.errorMessage}>
                                {errors.phone}
                              </span>}
                          </div>
                        </div>
                      </div>}

                  <div className={styles.cardSection}>
                    <h3 className={styles.subsectionTitle}>
                      {t("checkoutPage.paymentMethod.cardDetails")}
                    </h3>
                    {!isPortalLikeLayout && <CardElement className={styles.cardElement} />}
                  </div>
                </div>}
            </div>
          </div>}

          <div className={styles.hiddenActionsSpacer} />
          </form>
        </main>
      </div>

    </div>
  );
};
export default function CheckoutPage({
  onPaymentSuccess,
  onPaymentError,
  onCancel,
  onCreatePaymentIntent,
  onCreateSubscription
}) {
  const { t } = useTranslation("common");
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsListError, setPaymentMethodsListError] = useState(null);
  const [paymentStatusModal, setPaymentStatusModal] = useState({
    isOpen: false,
    paymentIntentId: null,
    paymentDetails: null
  });
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const data = await PaymentService.getPaymentMethods();
        if (data.success) {
          setPaymentMethodsListError(null);
          const savedDefaultPaymentMethod = localStorage.getItem("defaultPaymentMethod");
          const transformedMethods = data.paymentMethods.map((method, index) => ({
            id: method.id,
            type: method.type,
            brand: method.card.brand,
            last4: method.card.last4,
            expiryMonth: method.card.exp_month.toString().padStart(2, "0"),
            expiryYear: method.card.exp_year.toString(),
            billingInfo: method.billingInfo,
            isDefault:
              method.id === savedDefaultPaymentMethod ||
              (index === 0 && !savedDefaultPaymentMethod)
          }));
          setPaymentMethods(transformedMethods);
        } else {
          setPaymentMethods([]);
        }
      } catch (error) {
        logClientError("checkout.fetchPaymentMethods", error);
        setPaymentMethodsListError(t("checkoutPage.actionError"));
        setPaymentMethods([]);
      }
    };
    fetchPaymentMethods();
  }, [t]);
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("type")) {
      const details = {
        type: urlParams.get("type"),
        domain: urlParams.get("domain"),
        description: urlParams.get("description"),
        amount: Number.parseFloat(urlParams.get("amount")),
        currency: urlParams.get("currency"),
        years: Number.parseInt(urlParams.get("years")),
        planId: urlParams.get("planId"),
        requiresBillingInfo: urlParams.get("requiresBillingInfo") === "true",
        isSubscription: urlParams.get("isSubscription") === "true",
        isManagingMode: urlParams.get("isManagingMode"),
        existingPlanId: urlParams.get("existingPlanId"),
        existingStripeSubscriptionId: urlParams.get("existingStripeSubscriptionId"),
        projectTier: urlParams.get("projectTier"),
        projectId: urlParams.get("projectId"),
        tier: urlParams.get("tier"),
        status: urlParams.get("status"),
        paymentIntentId: urlParams.get("paymentIntentId"),
        clientSecret: urlParams.get("clientSecret"),
        customerName: "",
        customerEmail: "",
        planName: urlParams.get("planName"),
        productName: urlParams.get("productName"),
        existingProductName: urlParams.get("existingProductName"),
        billingCycle: urlParams.get("billingCycle"),
        planTier: urlParams.get("planTier"),
        currentExpirationDate: urlParams.get("currentExpirationDate"),
        domainId: urlParams.get("domainId"),
        pricePerYear: urlParams.get("pricePerYear"),
        renewalDate: urlParams.get("renewalDate") || undefined
      };
      setPaymentDetails(details);
    }
  }, []);
  const handlePaymentSuccess = result => {
    if (paymentDetails?.isSubscription && result?.subscriptionId) {
      setPaymentStatusModal({
        isOpen: true,
        paymentIntentId: null,
        paymentDetails: {
          ...paymentDetails,
          __forceStatus: "success",
          __message: "Subscription created successfully."
        }
      });
      return;
    }
    setPaymentStatusModal({
      isOpen: true,
      paymentIntentId: result.id,
      paymentDetails: paymentDetails
    });
    if (onPaymentSuccess) {
      onPaymentSuccess(result);
    }
  };
  const handlePaymentMethodRefresh = async () => {
    try {
      const data = await PaymentService.getPaymentMethods();
      if (data.success) {
        setPaymentMethodsListError(null);
        const transformedMethods = data.paymentMethods.map(method => ({
          id: method.id,
          type: method.type,
          brand: method.card.brand,
          last4: method.card.last4,
          expiryMonth: method.card.exp_month.toString().padStart(2, "0"),
          expiryYear: method.card.exp_year.toString(),
          isDefault: method.isDefault || false,
          billingInfo: method.billingInfo || null
        }));
        setPaymentMethods(transformedMethods);
      }
    } catch (error) {
      logClientError("checkout.refreshPaymentMethods", error);
      setPaymentMethodsListError(t("checkoutPage.actionError"));
    }
  };
  const handlePaymentError = error => {
    setPaymentStatusModal({
      isOpen: true,
      paymentIntentId: null,
      paymentDetails: {
        ...paymentDetails,
        __forceStatus: "error",
        __message: error?.message || "Payment failed. Please try again."
      }
    });
    if (onPaymentError) {
      onPaymentError(error);
    }
  };
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      window.location.href = "/billing";
    }
  };
  const handlePaymentStatusModalClose = () => {
    setPaymentStatusModal({
      isOpen: false,
      paymentIntentId: null,
      paymentDetails: null
    });
    window.location.href = "/billing";
  };
  const handlePaymentStatusSuccess = payment => {
    handlePaymentMethodRefresh();
  };
  const handlePaymentStatusError = error => {
    logClientError("checkout.paymentStatusModal", error);
  };
  if (!paymentDetails) {
    return (
      <div className={styles.checkoutContainer}>
        <div className={styles.loadingState}>
          <h2>Loading checkout...</h2>
        </div>
      </div>
    );
  }
  if (!stripePromise) {
    return (
      <div className={styles.checkoutContainer}>
        <div className={styles.loadingState}>
          <p>
            Payments are not configured. Set REACT_APP_STRIPE_PUBLISHABLE_KEY
            for this build.
          </p>
        </div>
      </div>
    );
  }
  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm
        paymentDetails={paymentDetails}
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentError={handlePaymentError}
        onCancel={handleCancel}
        paymentMethods={paymentMethods}
        onCreatePaymentIntent={onCreatePaymentIntent}
        onCreateSubscription={onCreateSubscription}
        onRefreshPaymentMethods={handlePaymentMethodRefresh}
        paymentMethodsListError={paymentMethodsListError}
      />

      <PaymentStatusModal
        isOpen={paymentStatusModal.isOpen}
        onClose={handlePaymentStatusModalClose}
        paymentIntentId={paymentStatusModal.paymentIntentId}
        paymentDetails={paymentStatusModal.paymentDetails}
        onSuccess={handlePaymentStatusSuccess}
        onError={handlePaymentStatusError}
        onRefreshPaymentMethods={handlePaymentMethodRefresh}
      />
    </Elements>
  );
}
